import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { warrantyPresetApi } from '../api/warrantyPresetApi';
import type {
  CreateWarrantyPresetDto,
  UpdateWarrantyPresetDto,
  UpdatePresetPartsDto,
} from '../types/warranty-preset.types';
import { useNotifications } from '@/contexts/NotificationContext';

/**
 * Hooks React Query pour la gestion des presets de garantie
 */

// ==================== QUERIES (lecture) ====================

/**
 * Récupérer les presets selon critères
 */
export const useWarrantyPresets = (
  vehicleType?: number,
  lang = 'fr',
) => {
  return useQuery({
    queryKey: ['warranty-presets', vehicleType, lang],
    queryFn: () => warrantyPresetApi.getPresetsByCriteria(vehicleType, lang),
    staleTime: 5 * 60 * 1000, // 5 minutes (ces données changent rarement)
  });
};

/**
 * Récupérer tous les presets (admin)
 */
export const useAllWarrantyPresets = () => {
  return useQuery({
    queryKey: ['warranty-presets', 'all'],
    queryFn: () => warrantyPresetApi.getAllPresets(),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Récupérer un preset par ID avec tous ses détails
 */
export const useWarrantyPreset = (id: number | undefined, lang = 'fr') => {
  return useQuery({
    queryKey: ['warranty-preset', id, lang],
    queryFn: () => warrantyPresetApi.getPresetById(id!, lang),
    enabled: id !== undefined,
    staleTime: 5 * 60 * 1000,
  });
};

// ==================== MUTATIONS (CRUD) ====================

/**
 * Créer un nouveau preset de garantie
 */
export const useCreateWarrantyPreset = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotifications();

  return useMutation({
    mutationFn: (preset: CreateWarrantyPresetDto) => warrantyPresetApi.createPreset(preset),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warranty-presets'] });
      showNotification('success', 'Preset de garantie créé avec succès');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Erreur lors de la création du preset';
      showNotification('error', message);
    },
  });
};

/**
 * Mettre à jour un preset de garantie
 */
export const useUpdateWarrantyPreset = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotifications();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateWarrantyPresetDto }) =>
      warrantyPresetApi.updatePreset(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['warranty-presets'] });
      queryClient.invalidateQueries({ queryKey: ['warranty-preset', variables.id] });
      showNotification('success', 'Preset de garantie mis à jour avec succès');
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || 'Erreur lors de la mise à jour du preset';
      showNotification('error', message);
    },
  });
};

/**
 * Mettre à jour les pièces d'un preset
 */
export const useUpdateWarrantyPresetParts = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotifications();

  return useMutation({
    mutationFn: ({ id, parts }: { id: number; parts: UpdatePresetPartsDto }) =>
      warrantyPresetApi.updatePresetParts(id, parts),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['warranty-presets'] });
      queryClient.invalidateQueries({ queryKey: ['warranty-preset', variables.id] });
      showNotification('success', 'Pièces du preset mises à jour avec succès');
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || 'Erreur lors de la mise à jour des pièces';
      showNotification('error', message);
    },
  });
};

/**
 * Supprimer un preset de garantie
 */
export const useDeleteWarrantyPreset = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotifications();

  return useMutation({
    mutationFn: (id: number) => warrantyPresetApi.deletePreset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warranty-presets'] });
      showNotification('success', 'Preset de garantie supprimé avec succès');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Erreur lors de la suppression du preset';
      showNotification('error', message);
    },
  });
};
